/**
 * The A/B/C design variant (REV30 — docs/specs/REV30-design-variants.md), mirroring
 * `src/lib/theme.ts`'s pattern.
 *
 * A design is a `data-design="<slug>"` attribute on `<html>`. Where a theme swaps only surface
 * colours, a design swaps the whole visual language: surface + accent defaults, fonts, the Tailwind
 * radius scale, textures, component-class treatments (`src/designs/<slug>.css`), the app shell and
 * the motion vocabulary (`src/designs/<slug>/`). Route logic is shared by all designs on purpose —
 * the test compares design, not features.
 *
 * `null` means "Classic" (design A): no attribute, exactly what shipped before REV30. All three stay
 * selectable, but the DEFAULT for anyone who has never chosen is `DEFAULT_DESIGN` (Toy Box — user
 * decision 2026-09-17), so choosing Classic is stored as the explicit sentinel `'classic'` rather
 * than as "nothing stored". The default itself is never persisted: it can be changed later and
 * everyone who hasn't picked follows it.
 *
 * Every non-Classic design ships a dark and a light MODE (`data-mode="dark|light"`, same element) so
 * the test never forces a light-theme user into the dark or vice versa — brightness would otherwise
 * confound the comparison. Mode is remembered per design; with nothing stored it follows the
 * brightness of the Classic theme the user explicitly picked, else the design's own default.
 *
 * ACCENT is per design too: Classic keeps its own stored accent (lib/accent.ts, untouched), and each
 * of B/C remembers its own override — or none, which means "whatever the active palette ships". A
 * Classic amber must not silently repaint the Ledger's gold leaf, and vice versa. Same mechanism as
 * Classic (an inline `--accent` on `<html>`, every tint derives from it via color-mix).
 *
 * Resolution order at boot: `?design=<slug>` (persisted, so a shared link opts someone in; `classic`
 * opts back out) → localStorage → `DEFAULT_DESIGN`. Nothing is sent to the server.
 */
import { useSyncExternalStore } from 'react';
import { applyStoredAccent, isHexColor } from './accent';
import { currentTheme, type ThemeSlug } from './theme';

export type DesignSlug = 'ledger' | 'toybox';
export type DesignMode = 'dark' | 'light';

/** The mode each design was drawn in first — what a visitor with no preference at all sees. */
export const DESIGN_DEFAULT_MODE: Record<DesignSlug, DesignMode> = { ledger: 'dark', toybox: 'light' };
const DEFAULT_MODE = DESIGN_DEFAULT_MODE;

type Swatch = { bg: string; elevated: string; text: string; accent: string };

/**
 * Per-design PALETTES (`data-palette="<slug>"`, same element). A palette is a colourway of one
 * design and always comes as a dark + light pair, so it composes with the mode toggle instead of
 * replacing it. `slug: null` is the design's original colourway (no attribute). Remembered per
 * design. `swatch` mirrors the stylesheet blocks — kept honest by designs.contrast.test.ts.
 */
export const DESIGN_PALETTES: Record<
  DesignSlug,
  ReadonlyArray<{ slug: string | null; name: string; swatch: Record<DesignMode, Swatch> }>
> = {
  ledger: [
    {
      slug: null,
      name: 'Ink & Gold',
      swatch: {
        dark: { bg: '#0b0d12', elevated: '#11141b', text: '#ece4d0', accent: '#d4a94a' },
        light: { bg: '#efe6d0', elevated: '#f7f0de', text: '#2b2115', accent: '#b8862b' },
      },
    },
    {
      slug: 'oxblood',
      name: 'Oxblood',
      swatch: {
        dark: { bg: '#140a0b', elevated: '#1d1012', text: '#f0e2d6', accent: '#cf9d4a' },
        light: { bg: '#f1e2d8', elevated: '#f9eee6', text: '#2e1512', accent: '#b3782a' },
      },
    },
    {
      slug: 'verdigris',
      name: 'Verdigris',
      swatch: {
        dark: { bg: '#08110f', elevated: '#0e1a17', text: '#e4ecdf', accent: '#d08c55' },
        light: { bg: '#e3eadf', elevated: '#eef3ea', text: '#14231c', accent: '#b9712f' },
      },
    },
    {
      slug: 'vellum',
      name: 'Royal Vellum',
      swatch: {
        dark: { bg: '#0f0b18', elevated: '#171124', text: '#ebe4f3', accent: '#d8b35a' },
        light: { bg: '#e9e3ef', elevated: '#f3eef7', text: '#1f1630', accent: '#b8862b' },
      },
    },
    {
      slug: 'walnut',
      name: 'Walnut',
      swatch: {
        dark: { bg: '#120d08', elevated: '#1b140d', text: '#f0e6d4', accent: '#d9a755' },
        light: { bg: '#ece0c8', elevated: '#f5ecd9', text: '#2a1e10', accent: '#a9741f' },
      },
    },
    {
      slug: 'slate',
      name: 'Slate',
      swatch: {
        dark: { bg: '#0c0e11', elevated: '#13161a', text: '#e6e9ec', accent: '#9fc3e0' },
        light: { bg: '#e4e7ea', elevated: '#f0f2f4', text: '#161b22', accent: '#6fa3cc' },
      },
    },
    {
      slug: 'lapis',
      name: 'Lapis',
      swatch: {
        dark: { bg: '#070c1a', elevated: '#0c1326', text: '#e6e9f5', accent: '#e0b64e' },
        light: { bg: '#e1e6f3', elevated: '#eef1f9', text: '#111a36', accent: '#b8862b' },
      },
    },
    {
      slug: 'ash',
      name: 'Ash & Vermilion',
      swatch: {
        dark: { bg: '#0b0b0b', elevated: '#131312', text: '#ecebe6', accent: '#e0563f' },
        light: { bg: '#ebeae4', elevated: '#f5f4ef', text: '#1a1917', accent: '#d9523c' },
      },
    },
  ],
  toybox: [
    {
      slug: null,
      name: 'Cream Soda',
      swatch: {
        dark: { bg: '#17152a', elevated: '#221f3b', text: '#f6ecd2', accent: '#ff6a3d' },
        light: { bg: '#fff3d6', elevated: '#ffffff', text: '#1d1a2b', accent: '#ff6a3d' },
      },
    },
    {
      slug: 'mint',
      name: 'Mint',
      swatch: {
        dark: { bg: '#0f2019', elevated: '#173026', text: '#e3f7ec', accent: '#ff5d8f' },
        light: { bg: '#dcf5e7', elevated: '#ffffff', text: '#14261e', accent: '#ff5d8f' },
      },
    },
    {
      slug: 'bubblegum',
      name: 'Bubblegum',
      swatch: {
        dark: { bg: '#24101f', elevated: '#341a2d', text: '#ffe9f2', accent: '#29c4d6' },
        light: { bg: '#ffe3ef', elevated: '#ffffff', text: '#2a1424', accent: '#29c4d6' },
      },
    },
    {
      slug: 'sky',
      name: 'Sky',
      swatch: {
        dark: { bg: '#0c1628', elevated: '#14223b', text: '#e6f0ff', accent: '#ffc21a' },
        light: { bg: '#dcecff', elevated: '#ffffff', text: '#101d33', accent: '#ffc21a' },
      },
    },
    {
      slug: 'grape',
      name: 'Grape',
      swatch: {
        dark: { bg: '#170f2b', elevated: '#22173d', text: '#efe8ff', accent: '#b6f03c' },
        light: { bg: '#ece3ff', elevated: '#ffffff', text: '#1e1436', accent: '#b6f03c' },
      },
    },
    {
      slug: 'lemon',
      name: 'Lemon',
      swatch: {
        dark: { bg: '#1c1a08', elevated: '#29260f', text: '#fffbe0', accent: '#4db8ff' },
        light: { bg: '#fff8c9', elevated: '#ffffff', text: '#2b2608', accent: '#4db8ff' },
      },
    },
    {
      slug: 'peach',
      name: 'Peach',
      swatch: {
        dark: { bg: '#25140c', elevated: '#351f14', text: '#ffeee3', accent: '#2fd1b2' },
        light: { bg: '#ffe6d6', elevated: '#ffffff', text: '#2e1a10', accent: '#2fd1b2' },
      },
    },
    {
      slug: 'pocket',
      name: 'Pocket',
      swatch: {
        dark: { bg: '#10160a', elevated: '#1a2311', text: '#e6efc4', accent: '#9bbc0f' },
        light: { bg: '#dfe8b8', elevated: '#f4f8dc', text: '#1f2b12', accent: '#8bac0f' },
      },
    },
  ],
};

/** Classic's light-surface themes (index.css `color-scheme: light` blocks). */
const LIGHT_THEMES: ReadonlySet<ThemeSlug> = new Set<ThemeSlug>(['day', 'sakura', 'mint', 'sky']);

const VALID_SLUGS: ReadonlySet<string> = new Set<DesignSlug>(['ledger', 'toybox']);

export const DESIGN_PRESETS: ReadonlyArray<{
  slug: DesignSlug | null;
  /** The A/B/C letter shown next to the name, so feedback can reference it unambiguously. */
  letter: string;
  name: string;
  note: string;
  swatch: { bg: string; elevated: string; text: string; accent: string };
}> = [
  {
    slug: null,
    letter: 'A',
    name: 'Classic',
    note: 'The original — rounded, lantern-lit, themeable.',
    swatch: { bg: '#0e0b08', elevated: '#171209', text: '#f2ece1', accent: '#ffa02e' },
  },
  {
    slug: 'ledger',
    letter: 'B',
    name: 'Arcane Ledger',
    note: "An adventurer's account book — ink, gold leaf, ruled columns.",
    swatch: { bg: '#0b0d12', elevated: '#11141b', text: '#ece4d0', accent: '#d4a94a' },
  },
  {
    slug: 'toybox',
    letter: 'C',
    name: 'Toy Box',
    note: 'Chunky outlines, hard shadows, springy sprites.',
    swatch: { bg: '#fff3d6', elevated: '#ffffff', text: '#1d1a2b', accent: '#ff6a3d' },
  },
];

/** What a visitor with no stored choice gets. `null` would make Classic the default again. */
export const DEFAULT_DESIGN: DesignSlug | null = 'toybox';

const STORAGE_KEY = 'hypermaple.design';
/** Stored when the user explicitly picks Classic — "nothing stored" now means "use the default". */
const CLASSIC_SENTINEL = 'classic';
const MODE_STORAGE_PREFIX = 'hypermaple.design.mode.';
const PALETTE_STORAGE_PREFIX = 'hypermaple.design.palette.';
const ACCENT_STORAGE_PREFIX = 'hypermaple.design.accent.';
const QUERY_PARAM = 'design';

function isDesignMode(value: string | null): value is DesignMode {
  return value === 'dark' || value === 'light';
}

/** Stored per-design choice → brightness of an explicitly chosen Classic theme → design default. */
function resolveMode(slug: DesignSlug): DesignMode {
  try {
    const stored = localStorage.getItem(MODE_STORAGE_PREFIX + slug);
    if (isDesignMode(stored)) {
      return stored;
    }
  } catch {
    // Ignore.
  }
  const theme = currentTheme();
  if (theme != null) {
    return LIGHT_THEMES.has(theme) ? 'light' : 'dark';
  }
  return DEFAULT_MODE[slug];
}

/** The stored palette for a design, if it still names one that exists (else the original, `null`). */
function resolvePalette(slug: DesignSlug): string | null {
  try {
    const stored = localStorage.getItem(PALETTE_STORAGE_PREFIX + slug);
    if (stored != null && DESIGN_PALETTES[slug].some((p) => p.slug === stored)) {
      return stored;
    }
  } catch {
    // Ignore.
  }
  return null;
}

/** The stored accent override for a design, or `null` for "follow the palette". */
function resolveAccent(slug: DesignSlug): string | null {
  try {
    const stored = localStorage.getItem(ACCENT_STORAGE_PREFIX + slug);
    if (stored != null && isHexColor(stored)) {
      return stored.toLowerCase();
    }
  } catch {
    // Ignore.
  }
  return null;
}

function setAccentInline(color: string | null): void {
  if (color != null) {
    document.documentElement.style.setProperty('--accent', color);
  } else {
    document.documentElement.style.removeProperty('--accent');
  }
}

function setPaletteAttribute(palette: string | null): void {
  if (palette != null) {
    document.documentElement.setAttribute('data-palette', palette);
  } else {
    document.documentElement.removeAttribute('data-palette');
  }
}

function isDesignSlug(value: string): value is DesignSlug {
  return VALID_SLUGS.has(value);
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reads the design currently applied to `<html>` (the attribute is the single source of truth). */
export function currentDesign(): DesignSlug | null {
  const value = document.documentElement.getAttribute('data-design');
  return value != null && isDesignSlug(value) ? value : null;
}

/**
 * Applies a design to `<html>`; `null` is Classic. `persist` (default) remembers it as the user's
 * explicit choice — boot passes `false` when it is only applying `DEFAULT_DESIGN`.
 */
export function applyDesign(slug: DesignSlug | null, persist = true): void {
  const root = document.documentElement;

  if (slug != null && isDesignSlug(slug)) {
    root.setAttribute('data-design', slug);
    root.setAttribute('data-mode', resolveMode(slug));
    setPaletteAttribute(resolvePalette(slug));
    setAccentInline(resolveAccent(slug));
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, slug);
      } catch {
        // Storage can be unavailable (private mode) -- the design still applies for this page load.
      }
    }
  } else {
    root.removeAttribute('data-design');
    root.removeAttribute('data-mode');
    root.removeAttribute('data-palette');
    // Hand `--accent` back to Classic: drop the design's override, then let accent.ts re-apply its own.
    setAccentInline(null);
    applyStoredAccent();
    if (persist) {
      try {
        localStorage.setItem(STORAGE_KEY, CLASSIC_SENTINEL);
      } catch {
        // Ignore, same as above.
      }
    }
  }
  listeners.forEach((listener) => listener());
}

/** Resolves `?design=`, then storage, then `DEFAULT_DESIGN`. Called once at startup, before render. */
export function applyStoredDesign(): void {
  const fromQuery = new URLSearchParams(window.location.search).get(QUERY_PARAM);
  if (fromQuery != null) {
    const normalized = fromQuery.toLowerCase();
    if (isDesignSlug(normalized)) {
      applyDesign(normalized);
      return;
    }
    if (normalized === 'classic' || normalized === 'a') {
      applyDesign(null);
      return;
    }
  }

  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage unavailable -- fall through to the default, unpersisted.
  }
  if (stored != null && isDesignSlug(stored)) {
    applyDesign(stored);
  } else if (stored === CLASSIC_SENTINEL) {
    applyDesign(null);
  } else {
    applyDesign(DEFAULT_DESIGN, false);
  }
}

/** The live design, re-rendering on change — the shell switch and the /settings picker use this. */
export function useDesign(): DesignSlug | null {
  return useSyncExternalStore(subscribe, currentDesign, () => null);
}

/** The active design's mode, or `null` under Classic (which has themes instead). */
export function currentDesignMode(): DesignMode | null {
  if (currentDesign() == null) {
    return null;
  }
  const value = document.documentElement.getAttribute('data-mode');
  return isDesignMode(value) ? value : null;
}

/** Switches the active design between dark and light and remembers it for that design. */
export function applyDesignMode(mode: DesignMode): void {
  const slug = currentDesign();
  if (slug == null) {
    return;
  }
  document.documentElement.setAttribute('data-mode', mode);
  try {
    localStorage.setItem(MODE_STORAGE_PREFIX + slug, mode);
  } catch {
    // Storage can be unavailable (private mode) -- the mode still applies for this page load.
  }
  listeners.forEach((listener) => listener());
}

export function useDesignMode(): DesignMode | null {
  return useSyncExternalStore(subscribe, currentDesignMode, () => null);
}

/** The active design's palette slug, or `null` for its original colourway (and under Classic). */
export function currentDesignPalette(): string | null {
  const slug = currentDesign();
  if (slug == null) {
    return null;
  }
  const value = document.documentElement.getAttribute('data-palette');
  return value != null && DESIGN_PALETTES[slug].some((p) => p.slug === value) ? value : null;
}

/** Switches the active design's palette and remembers it for that design. `null` = the original. */
export function applyDesignPalette(palette: string | null): void {
  const slug = currentDesign();
  if (slug == null) {
    return;
  }
  const valid = palette != null && DESIGN_PALETTES[slug].some((p) => p.slug === palette) ? palette : null;
  setPaletteAttribute(valid);
  try {
    if (valid != null) {
      localStorage.setItem(PALETTE_STORAGE_PREFIX + slug, valid);
    } else {
      localStorage.removeItem(PALETTE_STORAGE_PREFIX + slug);
    }
  } catch {
    // Storage can be unavailable (private mode) -- the palette still applies for this page load.
  }
  listeners.forEach((listener) => listener());
}

export function useDesignPalette(): string | null {
  return useSyncExternalStore(subscribe, currentDesignPalette, () => null);
}

/** The active design's accent override, or `null` when it follows its palette (and under Classic). */
export function currentDesignAccent(): string | null {
  if (currentDesign() == null) {
    return null;
  }
  const value = document.documentElement.style.getPropertyValue('--accent').trim().toLowerCase();
  return isHexColor(value) ? value : null;
}

/** Overrides the active design's accent and remembers it for that design. `null` = palette default. */
export function applyDesignAccent(color: string | null): void {
  const slug = currentDesign();
  if (slug == null) {
    return;
  }
  const valid = color != null && isHexColor(color) ? color.toLowerCase() : null;
  setAccentInline(valid);
  try {
    if (valid != null) {
      localStorage.setItem(ACCENT_STORAGE_PREFIX + slug, valid);
    } else {
      localStorage.removeItem(ACCENT_STORAGE_PREFIX + slug);
    }
  } catch {
    // Storage can be unavailable (private mode) -- the accent still applies for this page load.
  }
  listeners.forEach((listener) => listener());
}

export function useDesignAccent(): string | null {
  return useSyncExternalStore(subscribe, currentDesignAccent, () => null);
}
